// src/pages/inventory/PurchaseInvoice/CreateNewPathItem.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import BasicModal from "../../../components/BasicModal";
import FormModalActions from "../../../components/FormModalActions";
import {
  DENSE_FIELD_PROPS,
  DENSE_FORM_BODY_SX,
  FORM_MODAL_CONTENT_SX,
} from "../../../utils/formModalStyles";
import { createBOMInBaseTable, updateBOMInBaseTable, getBOMsFromBaseTable } from "../../../services/labService";
import { getAllInventory, updateInventory } from "../../../services/inventoryService";
import {
  getPathItemDisplayName,
  validateUniqueCatalogName,
} from "../../../utils/duplicateNameUtils";
import { handleFormattedNameBlur } from "../../../utils/nameFormatUtils";
import {
  PATH_ITEM_DETAILS_OPTIONS,
  buildPathItemFormValuesFromBom,
  buildPathItemSavePayload,
  getPathItemCostPreviewLabel,
  getPathItemDetailsFieldLabel,
  isEachPathItem,
  resolvePathItemQtyUnit,
  usesPathItemDetailsMultiplier,
} from "../../../utils/pathItemPricingUtils";

const emptyForm = {
  name: "",
  pathItemDetails: "",
  details: "",
  qtyUnit: "",
  requiredQuantity: "",
  cost: "",
  description: "",
};

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required").min(2, "Name should be at least 2 characters long"),
  pathItemDetails: Yup.string()
    .oneOf(PATH_ITEM_DETAILS_OPTIONS, "Invalid path item details")
    .required("Path item details are required"),
  details: Yup.number().when("pathItemDetails", {
    is: (value) => usesPathItemDetailsMultiplier(value),
    then: (schema) =>
      schema
        .typeError("Must be a number")
        .min(0.0001, "Must be greater than 0")
        .required("This field is required"),
    otherwise: (schema) => schema.nullable().transform((v, o) => (o === "" || o == null ? 1 : v)),
  }),
  qtyUnit: Yup.string().required("Unit Type is required"),
  requiredQuantity: Yup.number()
    .typeError("Required Quantity Per Test must be a number")
    .min(0, "Must be ≥ 0")
    .required("Required Quantity Per Test is required"),
  cost: Yup.number().typeError("Cost must be a number").min(0, "Must be ≥ 0").required("Cost is required"),
});

const CreateNewPathItem = ({ open, onClose, editingBom, refreshData }) => {
  const [form, setForm] = useState(emptyForm);
  const [relatedInventoryRecords, setRelatedInventoryRecords] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [existingPathItems, setExistingPathItems] = useState([]);

  useEffect(() => {
    if (!open) return;
    const loadPathItems = async () => {
      try {
        const data = await getBOMsFromBaseTable();
        setExistingPathItems(Array.isArray(data) ? data : []);
      } catch (error) {
        console.warn("[CreateNewPathItem] Could not load path items for duplicate check:", error);
        setExistingPathItems([]);
      }
    };
    loadPathItems();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editingBom) {
      const values = buildPathItemFormValuesFromBom(editingBom);
      setForm({
        name: values.name,
        pathItemDetails: values.pathItemDetails,
        details: values.details,
        qtyUnit: values.qtyUnit,
        requiredQuantity: values.requiredQuantity,
        cost: values.cost,
        description: values.description || "",
      });
      fetchRelatedInventory(editingBom.id);
    } else {
      setForm({ ...emptyForm });
      setRelatedInventoryRecords([]);
    }
  }, [editingBom, open]);

  const fetchRelatedInventory = async (bomId) => {
    if (!bomId) return;

    setLoadingInventory(true);
    try {
      let allInventory = await getAllInventory();

      // Defensive filtering: Filter by organizationId if available
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          const organizationId = parsedUser?.organizationId || localStorage.getItem("organizationId");
          if (organizationId && Number(organizationId) > 0) {
            // Import filterByOrganization dynamically
            const { filterByOrganization } = await import("../../../utils/organizationUtils");
            allInventory = filterByOrganization(allInventory, parsedUser);
          }
        } catch (e) {
          console.error("[CreateNewPathItem] Error parsing stored user:", e);
        }
      }

      // Filter inventory records that have this bomId
      const related = allInventory.filter((inv) => inv.bomId === bomId);
      setRelatedInventoryRecords(related);
    } catch (error) {
      console.error("Error fetching related inventory:", error);
      setRelatedInventoryRecords([]);
    } finally {
      setLoadingInventory(false);
    }
  };

  const formik = useFormik({
    initialValues: form,
    enableReinitialize: true,
    validationSchema,
    validate: (values) => {
      const errors = {};
      const duplicateError = validateUniqueCatalogName({
        name: values.name,
        items: existingPathItems,
        excludeId: editingBom?.id,
        getName: getPathItemDisplayName,
        getId: (item) => item?.id,
        entityLabel: "Path item",
      });
      if (duplicateError) {
        errors.name = duplicateError;
      }
      return errors;
    },
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setFieldError, setFieldTouched }) => {
      const payload = buildPathItemSavePayload(values, {
        existingDescription: editingBom?.description,
      });

      let pathItems = existingPathItems;
      try {
        const fresh = await getBOMsFromBaseTable();
        pathItems = Array.isArray(fresh) ? fresh : [];
        setExistingPathItems(pathItems);
      } catch (error) {
        console.warn("[CreateNewPathItem] Could not refresh path items for duplicate check:", error);
      }

      const duplicateError = validateUniqueCatalogName({
        name: values.name,
        items: pathItems,
        excludeId: editingBom?.id,
        getName: getPathItemDisplayName,
        getId: (item) => item?.id,
        entityLabel: "Path item",
      });
      if (duplicateError) {
        setFieldTouched("name", true, false);
        setFieldError("name", duplicateError);
        return;
      }

      try {
        // Step 1: Update or create the BOM record in base table (not organization-specific)
        if (editingBom?.id) {
          await updateBOMInBaseTable(editingBom.id, payload);

          // Step 2: If name was changed and there are related inventory records, update them too
          // (Inventory records are organization-specific, so they still use organization-specific update)
          if (values.name !== editingBom.name && relatedInventoryRecords.length > 0) {
            const updatePromises = relatedInventoryRecords.map((record) => {
              const updateData = {
                ...record,
                name: values.name, // Update name to match new BOM name
              };
              return updateInventory(record.id, updateData);
            });

            await Promise.all(updatePromises);
            console.log(`Updated ${relatedInventoryRecords.length} inventory records with new name`);
          }
        } else {
          await createBOMInBaseTable(payload);
        }

        // Call refresh callback if provided
        if (typeof refreshData === "function") {
          refreshData();
        }

        handleCloseModalState(true);
      } catch (e) {
        console.error("Error saving path item:", e);
        const message =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to save path item. Please try again.";
        if (/already exists|duplicate/i.test(message)) {
          setFieldTouched("name", true, false);
          setFieldError("name", message);
          return;
        }
        alert(message);
        handleCloseModalState(false);
      }
    },
  });

  const clearFormState = () => {
    setForm({ ...emptyForm });
    setRelatedInventoryRecords([]);
    formik.resetForm({ values: { ...emptyForm } });
  };

  const handleCloseModalState = (result = false) => {
    clearFormState();
    if (onClose) onClose(result);
  };

  const handlePathItemDetailsChange = (event) => {
    const selectedType = event.target.value;
    formik.setFieldValue("pathItemDetails", selectedType);
    formik.setFieldValue("qtyUnit", resolvePathItemQtyUnit(selectedType));
    formik.setFieldValue("details", isEachPathItem(selectedType) ? 1 : "");
    formik.setFieldValue("requiredQuantity", "");
    formik.setFieldValue("cost", "");
    formik.setFieldTouched("details", false, false);
    formik.setFieldTouched("requiredQuantity", false, false);
    formik.setFieldTouched("cost", false, false);
    formik.setFieldTouched("qtyUnit", false, false);
  };

  const costPreview = getPathItemCostPreviewLabel(
    formik.values.pathItemDetails,
    formik.values.cost,
    formik.values.details,
    formik.values.requiredQuantity
  );

  return (
    <BasicModal
      open={open}
      onClose={() => handleCloseModalState(false)}
      title={editingBom ? "Edit Path Item" : "Create Path Item"}
    >
      <Box sx={{ ...FORM_MODAL_CONTENT_SX, ...DENSE_FORM_BODY_SX }}>
        {/* Show information about related inventory records when editing */}
        {editingBom && relatedInventoryRecords.length > 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            This path item has <strong>{relatedInventoryRecords.length}</strong> related inventory
            record{relatedInventoryRecords.length !== 1 ? "s" : ""}.
            {formik.values.name !== editingBom.name && (
              <> Changing the name will also update all related inventory records.</>
            )}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={(e) =>
                  handleFormattedNameBlur(e, {
                    setFieldValue: formik.setFieldValue,
                    handleBlur: formik.handleBlur,
                  })
                }
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                size="small"
                margin="dense"
                error={formik.touched.pathItemDetails && Boolean(formik.errors.pathItemDetails)}
              >
                <InputLabel id="path-item-details-label">Path Item Details</InputLabel>
                <Select
                  labelId="path-item-details-label"
                  id="pathItemDetails"
                  name="pathItemDetails"
                  label="Path Item Details"
                  value={formik.values.pathItemDetails}
                  onChange={handlePathItemDetailsChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="">Select type</MenuItem>
                  {PATH_ITEM_DETAILS_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.pathItemDetails && formik.errors.pathItemDetails && (
                  <Typography color="error" variant="caption">
                    {formik.errors.pathItemDetails}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {usesPathItemDetailsMultiplier(formik.values.pathItemDetails) && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={getPathItemDetailsFieldLabel(formik.values.pathItemDetails)}
                  name="details"
                  value={formik.values.details}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.details && Boolean(formik.errors.details)}
                  helperText={formik.touched.details && formik.errors.details}
                  {...DENSE_FIELD_PROPS}
                  inputProps={{ min: 0, step: "any" }}
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Unit Type"
                name="qtyUnit"
                value={formik.values.qtyUnit}
                InputProps={{ readOnly: true }}
                error={formik.touched.qtyUnit && Boolean(formik.errors.qtyUnit)}
                helperText={formik.touched.qtyUnit && formik.errors.qtyUnit}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Cost"
                name="cost"
                value={formik.values.cost}
                onChange={formik.handleChange}
                error={formik.touched.cost && Boolean(formik.errors.cost)}
                helperText={formik.touched.cost && formik.errors.cost}
                {...DENSE_FIELD_PROPS}
                inputProps={{ min: 0, step: "any" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Required Quantity Per Test"
                name="requiredQuantity"
                value={formik.values.requiredQuantity}
                onChange={formik.handleChange}
                error={formik.touched.requiredQuantity && Boolean(formik.errors.requiredQuantity)}
                helperText={
                  (formik.touched.requiredQuantity && formik.errors.requiredQuantity) ||
                  costPreview ||
                  undefined
                }
                {...DENSE_FIELD_PROPS}
                inputProps={{ min: 0, step: "any" }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormModalActions
                onCancel={() => handleCloseModalState(false)}
                submitType="submit"
                submitLabel="Save"
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </BasicModal>
  );
};

export default CreateNewPathItem;
